import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { JourneyComparisonStatus } from '@/apps/difference_checker/types/enums';
import { localStorageKeys } from '@/utils/tokens';
import { saveFilterToLocalStorage } from '@/utils/localStorage';
import {
  getAllContractLeafPaths,
  getAllowedContractPaths,
} from '@/utils/options/contractOptionFilters';
import { getTodayDateString } from '@/utils/dateTime';

const PERSISTED_FILTER_FIELDS = ['contracts', 'flattenContracts'];

export interface DifferenceCheckerFiltersState {
  contracts: string[][];
  flattenContracts: string[][];
  dateFrom: string | null;
  dateTo: string | null;
  statuses: JourneyComparisonStatus[];
  lines: string[];
}

const allContractPaths = getAllContractLeafPaths();

export const initialDifferenceCheckerFiltersState: DifferenceCheckerFiltersState =
  {
    contracts: allContractPaths,
    flattenContracts: allContractPaths,
    dateFrom: getTodayDateString(),
    dateTo: getTodayDateString(),
    statuses: [],
    lines: [],
  };

const differenceCheckerFiltersSlice = createSlice({
  name: 'differenceCheckerFilters',
  initialState: initialDifferenceCheckerFiltersState,
  reducers: {
    initializeFiltersFromStorage(state, action) {
      const userContracts = action.payload.userContracts || [];
      const storedFilters = JSON.parse(
        localStorage.getItem(
          localStorageKeys.differenceCheckerFilters || '{}'
        ) || '{}'
      );

      const isNumericId = (val: string) => /^\d+$/.test(val);

      const validContracts = (storedFilters.contracts || []).filter(
        (contractPath: string[]) => {
          if (!contractPath || contractPath.length === 0) {
            return false;
          }
          const leaf = contractPath[contractPath.length - 1];
          if (leaf && isNumericId(leaf)) {
            return userContracts.includes(Number(leaf));
          }
          return (storedFilters.flattenContracts || []).some(
            (flatPath: string[]) => {
              if (
                flatPath.length >= contractPath.length &&
                flatPath
                  .slice(0, contractPath.length)
                  .every((v, i) => v === contractPath[i])
              ) {
                const flatLeaf = flatPath[flatPath.length - 1];
                if (!flatLeaf) {
                  return false;
                }
                return (
                  isNumericId(flatLeaf) &&
                  userContracts.includes(Number(flatLeaf))
                );
              }
              return false;
            }
          );
        }
      );

      const validFlattenContracts = (
        storedFilters.flattenContracts || []
      ).filter((contractPath: string[]) => {
        if (!contractPath || contractPath.length === 0) {
          return false;
        }
        const leaf = contractPath[contractPath.length - 1];
        if (!leaf) {
          return;
        }
        return isNumericId(leaf) && userContracts.includes(Number(leaf));
      });

      // Nothing surviving validation defaults to the user's assigned contracts,
      // not an empty selection.
      const allowedPaths = getAllowedContractPaths(userContracts);
      const hasStoredSelection =
        validContracts.length > 0 || validFlattenContracts.length > 0;

      const validatedFilters = hasStoredSelection
        ? {
            contracts: validContracts,
            flattenContracts: validFlattenContracts,
          }
        : {
            contracts: allowedPaths,
            flattenContracts: allowedPaths,
          };

      if (
        validContracts.length !== (storedFilters.contracts?.length || 0) ||
        validFlattenContracts.length !==
          (storedFilters.flattenContracts?.length || 0)
      ) {
        saveFilterToLocalStorage(
          localStorageKeys.differenceCheckerFilters,
          validatedFilters,
          PERSISTED_FILTER_FIELDS
        );
      }

      Object.assign(state, validatedFilters);
    },
    // Permissions can change after the filters were initialised, leaving a
    // stored selection that points at contracts the user lost access to. Those
    // paths no longer resolve against the pruned option tree, so the cascader
    // renders them as bare contract ids (CBP-1197). Deselect them instead.
    revalidateContracts(
      state,
      action: PayloadAction<{ userContracts: number[] }>
    ) {
      const userContracts = action.payload.userContracts || [];
      const isAssignedContract = (contractPath: string[]) => {
        const leaf = contractPath[contractPath.length - 1];
        return (
          !!leaf && /^\d+$/.test(leaf) && userContracts.includes(Number(leaf))
        );
      };

      const contracts = state.contracts.filter(isAssignedContract);
      const flattenContracts =
        state.flattenContracts.filter(isAssignedContract);

      if (
        contracts.length === state.contracts.length &&
        flattenContracts.length === state.flattenContracts.length
      ) {
        return;
      }

      const allowedPaths = getAllowedContractPaths(userContracts);
      const hasRemainingSelection =
        contracts.length > 0 || flattenContracts.length > 0;

      const revalidatedFilters = hasRemainingSelection
        ? { contracts, flattenContracts }
        : { contracts: allowedPaths, flattenContracts: allowedPaths };

      Object.assign(state, revalidatedFilters);
      saveFilterToLocalStorage(
        localStorageKeys.differenceCheckerFilters,
        revalidatedFilters,
        PERSISTED_FILTER_FIELDS
      );
    },
    setFilters(
      state,
      action: PayloadAction<Partial<DifferenceCheckerFiltersState>>
    ) {
      const contractsChanged =
        action.payload.contracts !== undefined ||
        action.payload.flattenContracts !== undefined;
      const updatedState = {
        ...state,
        ...action.payload,
        ...(contractsChanged && action.payload.lines === undefined
          ? { lines: [] }
          : {}),
      };
      saveFilterToLocalStorage(
        localStorageKeys.differenceCheckerFilters,
        updatedState,
        PERSISTED_FILTER_FIELDS
      );
      return updatedState;
    },
    resetContracts(state) {
      state.contracts = [];
      state.flattenContracts = [];
      saveFilterToLocalStorage(
        localStorageKeys.differenceCheckerFilters,
        state,
        PERSISTED_FILTER_FIELDS
      );
    },
    resetFilters(state, action: PayloadAction<{ userContracts: number[] }>) {
      const allowedPaths = getAllowedContractPaths(
        action.payload.userContracts
      );
      const defaultState = {
        contracts: allowedPaths,
        flattenContracts: allowedPaths,
        dateFrom: getTodayDateString(),
        dateTo: getTodayDateString(),
        statuses: [],
        lines: [],
      };
      Object.assign(state, defaultState);
      saveFilterToLocalStorage(
        localStorageKeys.differenceCheckerFilters,
        defaultState,
        PERSISTED_FILTER_FIELDS
      );
    },
  },
});

export const {
  initializeFiltersFromStorage,
  revalidateContracts,
  setFilters,
  resetContracts,
  resetFilters,
} = differenceCheckerFiltersSlice.actions;

export default differenceCheckerFiltersSlice.reducer;
